import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Chat,
  CHAT_STATUS,
  CHAT_TYPE,
  Class,
  Message,
  MESSAGE_STATUS,
  MESSAGE_TYPE,
  type MessageType,
  Notification,
  NOTIFICATION_PRIORITY,
  NOTIFICATION_TYPE,
  Query,
  QUERY_PRIORITY,
  QUERY_STATUS,
  School,
  Student,
  Teacher,
} from "../models";
import { redisService } from "../services/redis-service";
import { socketServer } from "../socket/socket-server";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class ChatController {
  /**
   * Create a student query (starts a chat)
   * @route POST /api/v1/student/queries
   * @access Private (Student)
   */
  async createQuery(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { category, subject, description, priority, attachments } =
        req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!category || !subject || !description) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: category, subject, description",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Create query
      const query = new Query({
        studentId: student._id,
        schoolId: student.schoolId,
        category,
        subject,
        description,
        priority: priority || QUERY_PRIORITY.MEDIUM,
        status: QUERY_STATUS.PENDING,
        attachments: attachments || [],
      });

      await query.save();

      // Create chat for this query
      const chat = new Chat({
        schoolId: student.schoolId,
        studentId: student._id,
        type: CHAT_TYPE.QUERY,
        status: CHAT_STATUS.ACTIVE,
        subject: subject,
        participants: [new mongoose.Types.ObjectId(userId)],
        unreadCount: { school: 1, student: 0, teacher: 0 },
      });

      await chat.save();

      // Link query to chat
      query.chatId = chat._id;
      await query.save();

      // Create notification for school
      await this.createNotification(
        student.schoolId.toString(),
        NOTIFICATION_TYPE.QUERY_RESPONSE,
        "New Student Query",
        `Student ${student.fullName} has submitted a new query: ${subject}`,
        NOTIFICATION_PRIORITY.MEDIUM,
        `/school/queries/${query.id}`,
      );

      logger.info(`Query created: ${query.id}`, {
        queryId: query.id,
        studentId: student.id,
        schoolId: student.schoolId,
      });

      res.status(201).json({
        success: true,
        message: "Query created successfully",
        data: {
          query: {
            id: query.id,
            category: query.category,
            subject: query.subject,
            description: query.description,
            priority: query.priority,
            status: query.status,
            chatId: query.chatId,
            createdAt: query.createdAt,
          },
          chat: {
            id: chat.id,
            type: chat.type,
            status: chat.status,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create query error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create query",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all chats for authenticated user
   * @route GET /api/v1/chats
   * @access Private
   */
  async getChats(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { status, type, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const statusStr = typeof status === "string" ? status : "all";
      const typeStr = typeof type === "string" ? type : "all";
      const userRole = role ?? "student";
      const cacheKey = `chats:${userRole}:${userId}:${statusStr}:${typeStr}:${pageNum}:${limitNum}`;

      // Try to get from cache first
      const cachedChats = await redisService.getCachedUserChats(
        userId,
        userRole,
      );
      if (cachedChats && !status && !type && pageNum === 1) {
        // Return cached data if it's the first page with no filters
        res.status(200).json({
          success: true,
          message: "Chats retrieved successfully (cached)",
          data: {
            chats: cachedChats.slice(0, limitNum),
            pagination: {
              page: pageNum,
              limit: limitNum,
              total: cachedChats.length,
              pages: Math.ceil(cachedChats.length / limitNum),
            },
          },
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        participants: new mongoose.Types.ObjectId(userId),
      };

      if (status) filterQuery.status = status;
      if (type) filterQuery.type = type;

      // Role-specific filtering
      switch (role) {
        case "student": {
          const studentId = await this.getStudentId(userId);
          if (studentId) filterQuery.studentId = studentId;
          break;
        }
        case "school": {
          if (req.user?.schoolId) {
            filterQuery.schoolId = new mongoose.Types.ObjectId(
              req.user.schoolId,
            );
          }
          break;
        }
        case "teacher": {
          const teacherId = await this.getTeacherId(userId);
          if (teacherId) filterQuery.teacherId = teacherId;
          break;
        }
        case "admin": {
          // Admin can see all chats
          break;
        }
        // No default
      }

      const skip = (pageNum - 1) * limitNum;

      // Optimized query with lean() for better performance
      const chats = await Chat.find({ ...filterQuery })
        .populate("student", "fullName studentId profilePicture")
        .populate("teacher", "fullName employeeId profilePicture")
        .populate("lastMessage", "content type createdAt")
        .sort({ lastMessageAt: -1 })
        .skip(skip)
        .limit(limitNum)
        .lean();

      const total = await Chat.countDocuments(filterQuery);

      // Cache the first page results
      if (pageNum === 1 && !status && !type) {
        await redisService.cacheUserChats(userId, userRole, chats, 300);
      }

      // Format response with enhanced data
      const formattedChats = chats.map((chat: any) => {
        const student = chat.student;
        const teacher = chat.teacher;
        const lastMessage = chat.lastMessage;
        return {
          id: chat._id.toString(),
          type: chat.type,
          status: chat.status,
          subject: chat.subject,
          name: chat.name, // Group chat name
          isGroup: chat.isGroup || false,
          avatar: chat.avatar,
          student: student
            ? {
                id: student._id?.toString() || student.id,
                fullName: student.fullName,
                studentId: student.studentId,
                profilePicture: student.profilePicture,
              }
            : null,
          teacher: teacher
            ? {
                id: teacher._id?.toString() || teacher.id,
                fullName: teacher.fullName,
                employeeId: teacher.employeeId,
                profilePicture: teacher.profilePicture,
              }
            : null,
          lastMessage: lastMessage
            ? {
                id: lastMessage._id?.toString() || lastMessage.id,
                content: lastMessage.content,
                type: lastMessage.type,
                createdAt: lastMessage.createdAt,
              }
            : null,
          lastMessageAt: chat.lastMessageAt,
          unreadCount:
            chat.unreadCount?.[role as keyof typeof chat.unreadCount] || 0,
          participantsCount: chat.participants?.length || 0,
          createdAt: chat.createdAt,
        };
      });

      res.status(200).json({
        success: true,
        message: "Chats retrieved successfully",
        data: {
          chats: formattedChats,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get chats error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve chats",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get messages for a chat
   * @route GET /api/v1/chats/:chatId/messages
   * @access Private
   */
  async getMessages(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { chatId } = req.params;
      const userId = req.user?.userId;
      const { page = 1, limit = 50, before } = req.query;

      if (!mongoose.Types.ObjectId.isValid(chatId)) {
        res.status(400).json({
          success: false,
          message: "Invalid chat ID",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Verify user has access to this chat
      const chat = await Chat.findById(chatId);
      if (!chat) {
        res.status(404).json({
          success: false,
          message: "Chat not found",
        } as APIResponse);
        return;
      }

      if (!chat.participants.some(p => p.toString() === userId)) {
        res.status(403).json({
          success: false,
          message: "Access denied to this chat",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        chatId: new mongoose.Types.ObjectId(chatId),
        isDeleted: false,
      };

      if (before) {
        filterQuery.createdAt = { $lt: new Date(before as string) };
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // Try to get from cache for first page
      let messages: any[] = [];
      if (pageNum === 1 && !before) {
        const cachedMessages = await redisService.getCachedChatMessages(chatId);
        if (cachedMessages && cachedMessages.length > 0) {
          messages = cachedMessages.slice(0, limitNum);
        }
      }

      // If not in cache, fetch from database
      if (messages.length === 0) {
        messages = await Message.find({ ...filterQuery })
          .populate("senderId", "email fullName profilePicture")
          .populate("replyTo", "content type senderId")
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNum)
          .lean();

        // Cache first page messages
        if (pageNum === 1 && !before) {
          await redisService.cacheChatMessages(chatId, messages, 600);
        }
      }

      // Mark messages as read
      const role = req.user?.role as "school" | "student" | "teacher" | "admin";
      await chat.markAsRead(role);
      await redisService.resetUnreadCount(chatId, userId);

      res.status(200).json({
        success: true,
        message: "Messages retrieved successfully",
        data: {
          messages: messages.reverse().map(msg => ({
            id: msg.id,
            senderId: msg.senderId,
            senderRole: msg.senderRole,
            type: msg.type,
            content: msg.content,
            attachments: msg.attachments,
            status: msg.status,
            replyTo: msg.replyTo
              ? {
                  id: msg.replyTo.id,
                  content: msg.replyTo.content,
                  type: msg.replyTo.type,
                }
              : null,
            isEdited: msg.isEdited,
            editedAt: msg.editedAt,
            readBy: msg.readBy,
            createdAt: msg.createdAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            hasMore: messages.length === limitNum,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get messages error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve messages",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Send a message
   * @route POST /api/v1/chats/:chatId/messages
   * @access Private
   */
  async sendMessage(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { chatId } = req.params;
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { content, type, replyTo, duration } = req.body;
      const parsedDuration =
        duration != null && duration !== ""
          ? Math.max(0, Number.parseInt(String(duration), 10))
          : undefined;

      // Handle file uploads from req.files
      const files = (req as any).files;
      let attachments: any[] = [];

      if (files) {
        // Process chatFiles or chatAttachments
        const chatFiles = files.chatFiles || files.chatAttachments || [];
        if (Array.isArray(chatFiles) && chatFiles.length > 0) {
          attachments = chatFiles.map((file: Express.Multer.File) => {
            const attachment: Record<string, unknown> = {
              fileName: file.filename,
              originalName: file.originalname,
              fileSize: file.size,
              mimeType: file.mimetype,
              fileUrl: `${process.env.IMAGE_URL || "http://localhost:3080/uploads"}/chat/${file.filename}`,
              uploadedAt: new Date(),
            };
            if (
              parsedDuration != null &&
              !Number.isNaN(parsedDuration) &&
              file.mimetype?.startsWith("audio/")
            ) {
              attachment.duration = parsedDuration;
            }
            return attachment;
          });
        }
      }

      // Also check if attachments are passed in body (for backward compatibility)
      if (req.body.attachments && Array.isArray(req.body.attachments)) {
        attachments = [...attachments, ...req.body.attachments];
      }

      if (!mongoose.Types.ObjectId.isValid(chatId)) {
        res.status(400).json({
          success: false,
          message: "Invalid chat ID",
        } as APIResponse);
        return;
      }

      if (!userId || !role) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!content && attachments.length === 0) {
        res.status(400).json({
          success: false,
          message: "Message content or attachments required",
        } as APIResponse);
        return;
      }

      // Determine message type based on attachments
      let messageType: MessageType = MESSAGE_TYPE.TEXT;
      if (
        typeof type === "string" &&
        Object.values(MESSAGE_TYPE).includes(type as MessageType)
      ) {
        messageType = type as MessageType;
      }
      if (attachments.length > 0) {
        const firstAttachment = attachments[0];
        if (firstAttachment.mimeType?.startsWith("image/")) {
          messageType = MESSAGE_TYPE.IMAGE;
        } else if (firstAttachment.mimeType?.startsWith("video/")) {
          messageType = MESSAGE_TYPE.VIDEO;
        } else if (firstAttachment.mimeType?.startsWith("audio/")) {
          messageType = MESSAGE_TYPE.AUDIO;
        } else {
          messageType = MESSAGE_TYPE.FILE;
        }
      }

      const chat = await Chat.findById(chatId);
      if (!chat) {
        res.status(404).json({
          success: false,
          message: "Chat not found",
        } as APIResponse);
        return;
      }

      if (!chat.participants.some(p => p.toString() === userId)) {
        res.status(403).json({
          success: false,
          message: "Access denied to this chat",
        } as APIResponse);
        return;
      }

      // Check if group chat and if only admins can post
      if (chat.isGroup && chat.settings?.onlyAdminsCanPost) {
        const isAdmin = chat.isAdmin(new mongoose.Types.ObjectId(userId));
        if (!isAdmin) {
          res.status(403).json({
            success: false,
            message: "Only admins can post in this group",
          } as APIResponse);
          return;
        }
      }

      // Create message
      const message = new Message({
        chatId: chat._id,
        senderId: new mongoose.Types.ObjectId(userId),
        senderRole: role as "school" | "student" | "teacher" | "admin",
        type: messageType,
        content: content || "",
        attachments: attachments.length > 0 ? attachments : undefined,
        status: MESSAGE_STATUS.SENT,
        replyTo: replyTo
          ? mongoose.Types.ObjectId.createFromHexString(replyTo)
          : undefined,
      });

      await message.save();

      // Update chat
      chat.lastMessage = message._id;
      chat.lastMessageAt = new Date();

      // Increment unread for all participants except sender
      const otherParticipants = chat.participants.filter(
        p => p.toString() !== userId,
      );
      for (const participant of otherParticipants) {
        // Get participant role (simplified - could be improved)
        const participantId = participant.toString();
        await redisService.incrementUnreadCount(chatId, participantId);
      }

      await chat.incrementUnread(
        role as "school" | "student" | "teacher" | "admin",
      );
      await chat.save();

      // Invalidate caches
      await redisService.invalidateChatMessages(chatId);
      await redisService.invalidateChat(chatId);
      for (const participant of chat.participants) {
        const participantRole = role; // Simplified - could get actual role
        await redisService.invalidateUserChats(
          participant.toString(),
          participantRole,
        );
      }

      // Create notification for other participants
      for (const participantId of otherParticipants) {
        await this.createNotification(
          participantId.toString(),
          NOTIFICATION_TYPE.CHAT_MESSAGE,
          "New Message",
          content || "You received a new message",
          NOTIFICATION_PRIORITY.MEDIUM,
          `/chats/${chatId}`,
        );
      }

      await message.populate("senderId", "email fullName profilePicture");
      if (replyTo) {
        await message.populate("replyTo", "content type");
      }

      // Emit socket event for real-time delivery
      try {
        socketServer.emitToChat(chatId, "new_message", {
          message: {
            id: message.id,
            chatId: message.chatId,
            senderId: message.senderId,
            senderRole: message.senderRole,
            type: message.type,
            content: message.content,
            attachments: message.attachments,
            status: message.status,
            replyTo: message.replyTo
              ? {
                  id: (message.replyTo as any).id,
                  content: (message.replyTo as any).content,
                  type: (message.replyTo as any).type,
                }
              : null,
            createdAt: message.createdAt,
            sender: (message.senderId as any).email
              ? {
                  id:
                    (message.senderId as any).id ||
                    (message.senderId as any)._id,
                  email: (message.senderId as any).email,
                  fullName: (message.senderId as any).fullName,
                  profilePicture: (message.senderId as any).profilePicture,
                }
              : null,
          },
        });
        socketServer.emitToChat(chatId, "message.new", {
          id: message.id,
          conversationId: chatId,
          senderId: userId,
          type: message.type,
          content: message.content,
          createdAt: message.createdAt,
        });

        // Check if recipients are online and mark as delivered
        const recipients = chat.participants.filter(
          p => p.toString() !== userId,
        );

        for (const recipient of recipients) {
          const recipientId = recipient.toString();
          const isOnline = await redisService.isUserOnline(recipientId);

          if (isOnline) {
            // Mark as delivered if recipient is online
            message.status = MESSAGE_STATUS.DELIVERED;
            await message.save();

            // Emit delivered status
            socketServer.emitToUser(recipientId, "message_delivered", {
              messageId: message.id,
              chatId: chatId,
            });
          }
        }

        // Cache message
        await redisService.cacheMessage(chatId, message.id, {
          id: message.id,
          content: message.content,
          senderId: message.senderId,
          createdAt: message.createdAt,
        });

        // Invalidate chat cache for participants
        for (const participant of chat.participants) {
          const participantId = participant.toString();
          const participantRole = role; // Could be improved to get actual role
          await redisService.invalidateUserChats(
            participantId,
            participantRole,
          );
        }
      } catch (socketError) {
        logger.error("Error emitting socket event:", socketError);
        // Continue with response even if socket fails
      }

      logger.info(`Message sent: ${message.id}`, {
        messageId: message.id,
        chatId: chat.id,
        senderId: userId,
      });

      res.status(201).json({
        success: true,
        message: "Message sent successfully",
        data: {
          message: {
            id: message.id,
            senderId: message.senderId,
            senderRole: message.senderRole,
            type: message.type,
            content: message.content,
            attachments: message.attachments,
            status: message.status,
            replyTo: message.replyTo
              ? {
                  id: (message.replyTo as any).id,
                  content: (message.replyTo as any).content,
                  type: (message.replyTo as any).type,
                }
              : null,
            createdAt: message.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Send message error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to send message",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create or get chat between student and school/teacher
   * @route POST /api/v1/chats
   * @access Private
   */
  async createOrGetChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { studentId, teacherId, type, subject } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      let chat: any;

      switch (role) {
        case "student": {
          const student = await Student.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (!student) {
            res.status(404).json({
              success: false,
              message: "Student profile not found",
            } as APIResponse);
            return;
          }

          if (teacherId) {
            // Student-Teacher chat
            chat = await Chat.findOne({
              studentId: student._id,
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              teacherId: new mongoose.Types.ObjectId(teacherId),
              type: CHAT_TYPE.STUDENT_TEACHER,
              status: { $ne: CHAT_STATUS.CLOSED },
            });

            if (!chat) {
              chat = new Chat({
                schoolId: student.schoolId,
                studentId: student._id,
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                teacherId: new mongoose.Types.ObjectId(teacherId),
                type: CHAT_TYPE.STUDENT_TEACHER,
                status: CHAT_STATUS.ACTIVE,
                subject: subject || "Student-Teacher Chat",
                participants: [
                  // eslint-disable-next-line @typescript-eslint/no-deprecated
                  new mongoose.Types.ObjectId(userId),
                  await this.getTeacherUserId(teacherId),
                ],
                unreadCount: { school: 0, student: 0, teacher: 0 },
              });
              await chat.save();
            }
          } else {
            // Student-School chat
            chat = await Chat.findOne({
              studentId: student._id,
              schoolId: student.schoolId,
              type: CHAT_TYPE.SCHOOL_STUDENT,
              status: { $ne: CHAT_STATUS.CLOSED },
            });

            if (!chat) {
              chat = new Chat({
                schoolId: student.schoolId,
                studentId: student._id,
                type: CHAT_TYPE.SCHOOL_STUDENT,
                status: CHAT_STATUS.ACTIVE,
                subject: subject || "Student-School Chat",
                participants: [new mongoose.Types.ObjectId(userId)],
                unreadCount: { school: 0, student: 0, teacher: 0 },
              });
              await chat.save();
            }
          }

          break;
        }
        case "school": {
          if (!studentId) {
            res.status(400).json({
              success: false,
              message: "Student ID is required",
            } as APIResponse);
            return;
          }

          chat = await Chat.findOne({
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            studentId: new mongoose.Types.ObjectId(studentId),
            schoolId: new mongoose.Types.ObjectId(req.user?.schoolId),
            type: CHAT_TYPE.SCHOOL_STUDENT,
            status: { $ne: CHAT_STATUS.CLOSED },
          });

          if (!chat) {
            const student = await Student.findById(studentId);
            if (!student) {
              res.status(404).json({
                success: false,
                message: "Student not found",
              } as APIResponse);
              return;
            }

            chat = new Chat({
              schoolId: student.schoolId,
              studentId: student._id,
              type: CHAT_TYPE.SCHOOL_STUDENT,
              status: CHAT_STATUS.ACTIVE,
              subject: subject || "School-Student Chat",
              participants: [
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                new mongoose.Types.ObjectId(userId),
                student.userId,
              ],
              unreadCount: { school: 0, student: 0, teacher: 0 },
            });
            await chat.save();
          }

          break;
        }
        case "teacher": {
          if (!studentId) {
            res.status(400).json({
              success: false,
              message: "Student ID is required",
            } as APIResponse);
            return;
          }

          const teacher = await Teacher.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (!teacher) {
            res.status(404).json({
              success: false,
              message: "Teacher profile not found",
            } as APIResponse);
            return;
          }

          chat = await Chat.findOne({
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            studentId: new mongoose.Types.ObjectId(studentId),
            teacherId: teacher._id,
            type: CHAT_TYPE.STUDENT_TEACHER,
            status: { $ne: CHAT_STATUS.CLOSED },
          });

          if (!chat) {
            const student = await Student.findById(studentId);
            if (!student) {
              res.status(404).json({
                success: false,
                message: "Student not found",
              } as APIResponse);
              return;
            }

            chat = new Chat({
              schoolId: teacher.schoolId,
              studentId: student._id,
              teacherId: teacher._id,
              type: CHAT_TYPE.STUDENT_TEACHER,
              status: CHAT_STATUS.ACTIVE,
              subject: subject || "Student-Teacher Chat",
              participants: [
                new mongoose.Types.ObjectId(userId),
                student.userId,
              ],
              unreadCount: { school: 0, student: 0, teacher: 0 },
            });
            await chat.save();
          }

          break;
        }
        // No default
      }

      if (!chat) {
        res.status(400).json({
          success: false,
          message: "Unable to create chat for this role",
        } as APIResponse);
        return;
      }

      const populatedChat = await Chat.findById(chat._id)
        .populate("student", "fullName studentId profilePicture")
        .populate("teacher", "fullName employeeId profilePicture");

      const chatDoc = populatedChat as any;
      const student = chatDoc?.student;
      const teacher = chatDoc?.teacher;

      res.status(200).json({
        success: true,
        message: "Chat retrieved successfully",
        data: {
          chat: {
            id: chat.id,
            type: chat.type,
            status: chat.status,
            subject: chat.subject,
            student: student
              ? {
                  id: student.id || student._id,
                  fullName: student.fullName,
                  studentId: student.studentId,
                }
              : null,
            teacher: teacher
              ? {
                  id: teacher.id || teacher._id,
                  fullName: teacher.fullName,
                  employeeId: teacher.employeeId,
                }
              : null,
            createdAt: chat.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create or get chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create or get chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create chat between student and admin
   * @route POST /api/v1/chats/student-admin
   * @access Private (Student, Admin)
   */
  async createStudentAdminChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { subject } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (role === "student") {
        const student = await Student.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student profile not found",
          } as APIResponse);
          return;
        }

        // Check if chat already exists
        let chat = await Chat.findOne({
          studentId: student._id,
          type: CHAT_TYPE.STUDENT_ADMIN,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          // Find admin user (first admin in system - could be improved)
          const adminUser = await mongoose
            .model("User")
            .findOne({ role: "admin" })
            .lean();

          if (!adminUser) {
            res.status(404).json({
              success: false,
              message: "Admin not found",
            } as APIResponse);
            return;
          }

          chat = new Chat({
            studentId: student._id,
            type: CHAT_TYPE.STUDENT_ADMIN,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || "Student-Admin Chat",
            participants: [
              new mongoose.Types.ObjectId(userId),
              (adminUser as any)._id as mongoose.Types.ObjectId,
            ],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }

        const populatedChat = await Chat.findById(chat._id)
          .populate("student", "fullName studentId profilePicture")
          .lean();

        res.status(200).json({
          success: true,
          message: "Chat created successfully",
          data: { chat: populatedChat },
        } as APIResponse);
      } else {
        res.status(403).json({
          success: false,
          message: "Only students can initiate admin chats",
        } as APIResponse);
      }
    } catch (error: any) {
      logger.error("Create student-admin chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create chat between teacher and school
   * @route POST /api/v1/chats/teacher-school
   * @access Private (Teacher, School)
   */
  async createTeacherSchoolChat(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { subject } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (role === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });
        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher profile not found",
          } as APIResponse);
          return;
        }

        // Check if chat already exists
        let chat = await Chat.findOne({
          teacherId: teacher._id,
          schoolId: teacher.schoolId,
          type: CHAT_TYPE.TEACHER_SCHOOL,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          // Find school admin user
          const school = await mongoose
            .model("School")
            .findById(teacher.schoolId)
            .lean();
          if (!school) {
            res.status(404).json({
              success: false,
              message: "School not found",
            } as APIResponse);
            return;
          }

          const schoolUser = await mongoose
            .model("User")
            .findById((school as any).userId)
            .lean();

          if (!schoolUser) {
            res.status(404).json({
              success: false,
              message: "School user not found",
            } as APIResponse);
            return;
          }

          chat = new Chat({
            schoolId: teacher.schoolId,
            teacherId: teacher._id,
            type: CHAT_TYPE.TEACHER_SCHOOL,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || "Teacher-School Chat",
            participants: [
              new mongoose.Types.ObjectId(userId),
              (schoolUser as any)._id as mongoose.Types.ObjectId,
            ],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }

        const populatedChat = await Chat.findById(chat._id)
          .populate("teacher", "fullName employeeId profilePicture")
          .lean();

        res.status(200).json({
          success: true,
          message: "Chat created successfully",
          data: { chat: populatedChat },
        } as APIResponse);
      } else {
        res.status(403).json({
          success: false,
          message: "Only teachers can initiate school chats",
        } as APIResponse);
      }
    } catch (error: any) {
      logger.error("Create teacher-school chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create chat between school and admin
   * @route POST /api/v1/chats/school-admin
   * @access Private (School, Admin)
   */
  async createSchoolAdminChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { subject } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (role === "school") {
        const schoolId = req.user?.schoolId;
        if (!schoolId) {
          res.status(400).json({
            success: false,
            message: "School ID is required",
          } as APIResponse);
          return;
        }

        // Check if chat already exists
        let chat = await Chat.findOne({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          type: CHAT_TYPE.SCHOOL_ADMIN,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          // Find admin user
          const adminUser = await mongoose
            .model("User")
            .findOne({ role: "admin" })
            .lean();

          if (!adminUser) {
            res.status(404).json({
              success: false,
              message: "Admin not found",
            } as APIResponse);
            return;
          }

          chat = new Chat({
            schoolId: new mongoose.Types.ObjectId(schoolId),
            type: CHAT_TYPE.SCHOOL_ADMIN,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || "School-Admin Chat",
            participants: [
              new mongoose.Types.ObjectId(userId),
              (adminUser as any)._id as mongoose.Types.ObjectId,
            ],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }

        res.status(200).json({
          success: true,
          message: "Chat created successfully",
          data: { chat },
        } as APIResponse);
      } else {
        res.status(403).json({
          success: false,
          message: "Only schools can initiate admin chats",
        } as APIResponse);
      }
    } catch (error: any) {
      logger.error("Create school-admin chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create group chat
   * @route POST /api/v1/chats/group
   * @access Private
   */
  async createGroupChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const { name, description, participantIds, avatar } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!name) {
        res.status(400).json({
          success: false,
          message: "Group name is required",
        } as APIResponse);
        return;
      }

      if (
        !participantIds ||
        !Array.isArray(participantIds) ||
        participantIds.length === 0
      ) {
        res.status(400).json({
          success: false,
          message: "At least one participant is required",
        } as APIResponse);
        return;
      }

      // Add creator to participants
      const allParticipants = [
        new mongoose.Types.ObjectId(userId),
        ...participantIds.map((id: string) =>
          mongoose.Types.ObjectId.createFromHexString(id),
        ),
      ];

      // Remove duplicates
      const uniqueParticipants = [
        ...new Set(allParticipants.map(p => p.toString())),
      ].map(id => mongoose.Types.ObjectId.createFromHexString(id));

      const chat = new Chat({
        type: CHAT_TYPE.GROUP,
        status: CHAT_STATUS.ACTIVE,
        name,
        description,
        avatar,
        isGroup: true,
        createdBy: new mongoose.Types.ObjectId(userId),
        participants: uniqueParticipants,
        admins: [new mongoose.Types.ObjectId(userId)], // Creator is admin
        unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
        settings: {
          allowInvites: true,
          onlyAdminsCanPost: false,
        },
      });

      await chat.save();

      const userRole = role ?? "student";
      // Invalidate cache for all participants
      for (const participant of uniqueParticipants) {
        await redisService.invalidateUserChats(
          participant.toString(),
          userRole,
        );
      }

      const populatedChat = await Chat.findById(chat._id)
        .populate("participants", "email fullName profilePicture")
        .populate("createdBy", "email fullName")
        .lean();

      res.status(201).json({
        success: true,
        message: "Group chat created successfully",
        data: { chat: populatedChat },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create group chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create group chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Add participants to group chat
   * @route POST /api/v1/chats/:chatId/participants
   * @access Private
   */
  async addParticipants(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { chatId } = req.params;
      const userId = req.user?.userId;
      const { participantIds } = req.body;

      if (!mongoose.Types.ObjectId.isValid(chatId)) {
        res.status(400).json({
          success: false,
          message: "Invalid chat ID",
        } as APIResponse);
        return;
      }

      const chat = await Chat.findById(chatId);
      if (!chat) {
        res.status(404).json({
          success: false,
          message: "Chat not found",
        } as APIResponse);
        return;
      }

      if (!chat.isGroup) {
        res.status(400).json({
          success: false,
          message: "This is not a group chat",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }
      if (!chat.isParticipant(new mongoose.Types.ObjectId(userId))) {
        res.status(403).json({
          success: false,
          message: "You are not a participant of this chat",
        } as APIResponse);
        return;
      }

      if (
        !chat.settings?.allowInvites &&
        !chat.isAdmin(new mongoose.Types.ObjectId(userId))
      ) {
        res.status(403).json({
          success: false,
          message: "Only admins can add participants",
        } as APIResponse);
        return;
      }

      const newParticipants = participantIds.map((id: string) =>
        mongoose.Types.ObjectId.createFromHexString(id),
      );

      for (const participantId of newParticipants) {
        await chat.addParticipant(participantId);
      }

      await chat.save();

      // Invalidate cache
      await redisService.invalidateChat(chatId);
      for (const participant of chat.participants) {
        const participantRole = req.user?.role;
        if (participantRole) {
          await redisService.invalidateUserChats(
            participant.toString(),
            participantRole,
          );
        }
      }

      res.status(200).json({
        success: true,
        message: "Participants added successfully",
        data: { chat },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Add participants error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to add participants",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Remove participant from group chat
   * @route DELETE /api/v1/chats/:chatId/participants/:participantId
   * @access Private
   */
  async removeParticipant(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { chatId, participantId } = req.params;
      const userId = req.user?.userId;

      if (
        !mongoose.Types.ObjectId.isValid(chatId) ||
        !mongoose.Types.ObjectId.isValid(participantId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid chat or participant ID",
        } as APIResponse);
        return;
      }

      const chat = await Chat.findById(chatId);
      if (!chat) {
        res.status(404).json({
          success: false,
          message: "Chat not found",
        } as APIResponse);
        return;
      }

      if (!chat.isGroup) {
        res.status(400).json({
          success: false,
          message: "This is not a group chat",
        } as APIResponse);
        return;
      }

      const participantObjectId =
        mongoose.Types.ObjectId.createFromHexString(participantId);
      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }
      const userObjectId = new mongoose.Types.ObjectId(userId);

      // Allow removal if user is admin or removing themselves
      if (
        !chat.isAdmin(userObjectId) &&
        !participantObjectId.equals(userObjectId)
      ) {
        res.status(403).json({
          success: false,
          message: "Only admins can remove other participants",
        } as APIResponse);
        return;
      }

      await chat.removeParticipant(participantObjectId);
      await chat.save();

      // Invalidate cache
      await redisService.invalidateChat(chatId);
      await redisService.invalidateUserChats(
        participantId,
        req.user?.role || "student",
      );

      res.status(200).json({
        success: true,
        message: "Participant removed successfully",
        data: { chat },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Remove participant error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to remove participant",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  // Helper methods
  private async getStudentId(
    userId: string,
  ): Promise<mongoose.Types.ObjectId | null> {
    const student = await Student.findOne({
      userId: new mongoose.Types.ObjectId(userId),
    });
    return student ? student._id : null;
  }

  private async getTeacherId(
    userId: string,
  ): Promise<mongoose.Types.ObjectId | null> {
    const teacher = await Teacher.findOne({
      userId: new mongoose.Types.ObjectId(userId),
    });
    return teacher ? teacher._id : null;
  }

  private async getTeacherUserId(
    teacherId: string,
  ): Promise<mongoose.Types.ObjectId> {
    const teacher = await Teacher.findById(teacherId);
    return teacher ? teacher.userId : new mongoose.Types.ObjectId();
  }

  private async createNotification(
    userId: string,
    type: string,
    title: string,
    message: string,
    priority: string,
    actionUrl?: string,
  ): Promise<void> {
    try {
      const notification = new Notification({
        userId: new mongoose.Types.ObjectId(userId),
        type: type as any,
        title,
        message,
        priority: priority as any,
        actionUrl,
        isRead: false,
      });
      await notification.save();
    } catch (error) {
      logger.error("Failed to create notification:", error);
    }
  }

  /**
   * Get filtered teachers for school panel chat creation
   * @route GET /api/v1/school/chats/teachers
   * @access Private (School)
   */
  async getSchoolTeachersForChat(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { search, page = 1, limit = 20 } = req.query;

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated or school context missing",
        } as APIResponse);
        return;
      }

      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      const query: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: "active",
        isApproved: true,
      };

      // Search filter by name or email
      if (search && typeof search === "string") {
        const searchRegex = { $regex: search, $options: "i" };
        query.$or = [
          { fullName: searchRegex },
          { email: searchRegex },
          { employeeId: searchRegex },
        ];
      }

      const [teachers, total] = await Promise.all([
        Teacher.find({ ...query })
          .populate("userId", "email")
          .select("fullName employeeId profilePicture email phone")
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Teacher.countDocuments(query),
      ]);

      const formattedTeachers = teachers.map((teacher: any) => ({
        id: teacher._id.toString(),
        fullName: teacher.fullName,
        employeeId: teacher.employeeId,
        email: teacher.userId?.email || teacher.email,
        profilePicture: teacher.profilePicture,
        phone: teacher.phone,
      }));

      res.status(200).json({
        success: true,
        message: "Teachers retrieved successfully",
        data: {
          teachers: formattedTeachers,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get school teachers for chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve teachers",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get filtered students for school panel chat creation
   * @route GET /api/v1/school/chats/students
   * @access Private (School)
   */
  async getSchoolStudentsForChat(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { search, page = 1, limit = 20 } = req.query;

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated or school context missing",
        } as APIResponse);
        return;
      }

      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      const query: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: "active",
        isApproved: true,
      };

      // Search filter by name or email
      if (search && typeof search === "string") {
        const searchRegex = { $regex: search, $options: "i" };
        query.$or = [
          { fullName: searchRegex },
          { email: searchRegex },
          { studentId: searchRegex },
        ];
      }

      const [students, total] = await Promise.all([
        Student.find({ ...query })
          .populate("userId", "email")
          .select("fullName studentId profilePicture email phone academicLevel")
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Student.countDocuments(query),
      ]);

      const formattedStudents = students.map((student: any) => ({
        id: student._id.toString(),
        fullName: student.fullName,
        studentId: student.studentId,
        email: student.userId?.email || student.email,
        profilePicture: student.profilePicture,
        phone: student.phone,
        academicLevel: student.academicLevel,
      }));

      res.status(200).json({
        success: true,
        message: "Students retrieved successfully",
        data: {
          students: formattedStudents,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get school students for chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve students",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create chat from school panel with teacher or student
   * @route POST /api/v1/school/chats
   * @access Private (School)
   */
  async createSchoolChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { teacherId, studentId, subject } = req.body;

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated or school context missing",
        } as APIResponse);
        return;
      }

      if (!teacherId && !studentId) {
        res.status(400).json({
          success: false,
          message: "Either teacherId or studentId is required",
        } as APIResponse);
        return;
      }

      let chat: any;

      if (teacherId) {
        // School-Teacher chat
        const teacher = await Teacher.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(teacherId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        });

        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher not found or does not belong to your school",
          } as APIResponse);
          return;
        }

        chat = await Chat.findOne({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          teacherId: teacher._id,
          type: CHAT_TYPE.TEACHER_SCHOOL,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          chat = new Chat({
            schoolId: new mongoose.Types.ObjectId(schoolId),
            teacherId: teacher._id,
            type: CHAT_TYPE.TEACHER_SCHOOL,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || `Chat with ${teacher.fullName}`,
            participants: [new mongoose.Types.ObjectId(userId), teacher.userId],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }
      } else if (studentId) {
        // School-Student chat
        const student = await Student.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(studentId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        });

        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student not found or does not belong to your school",
          } as APIResponse);
          return;
        }

        chat = await Chat.findOne({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          studentId: student._id,
          type: CHAT_TYPE.SCHOOL_STUDENT,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          chat = new Chat({
            schoolId: new mongoose.Types.ObjectId(schoolId),
            studentId: student._id,
            type: CHAT_TYPE.SCHOOL_STUDENT,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || `Chat with ${student.fullName}`,
            participants: [new mongoose.Types.ObjectId(userId), student.userId],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }
      }

      const populatedChat = await Chat.findById(chat._id)
        .populate("student", "fullName studentId profilePicture")
        .populate("teacher", "fullName employeeId profilePicture")
        .lean();

      const chatDoc = populatedChat as any;
      const student = chatDoc?.student;
      const teacher = chatDoc?.teacher;

      res.status(200).json({
        success: true,
        message: "Chat created successfully",
        data: {
          chat: {
            id: chat.id,
            type: chat.type,
            status: chat.status,
            subject: chat.subject,
            student: student
              ? {
                  id: student.id || student._id,
                  fullName: student.fullName,
                  studentId: student.studentId,
                  profilePicture: student.profilePicture,
                }
              : null,
            teacher: teacher
              ? {
                  id: teacher.id || teacher._id,
                  fullName: teacher.fullName,
                  employeeId: teacher.employeeId,
                  profilePicture: teacher.profilePicture,
                }
              : null,
            createdAt: chat.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create school chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get filtered teachers for admin panel chat creation
   * @route GET /api/v1/admin/chats/teachers
   * @access Private (Admin)
   */
  async getAdminTeachersForChat(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { search, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      const query: any = {
        status: "active",
        isApproved: true,
      };

      // Search filter by name or email
      if (search && typeof search === "string") {
        const searchRegex = { $regex: search, $options: "i" };
        query.$or = [
          { fullName: searchRegex },
          { email: searchRegex },
          { employeeId: searchRegex },
        ];
      }

      const [teachers, total] = await Promise.all([
        Teacher.find({ ...query })
          .populate("userId", "email")
          .populate("schoolId", "schoolName")
          .select("fullName employeeId profilePicture email phone schoolId")
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Teacher.countDocuments(query),
      ]);

      const formattedTeachers = teachers.map((teacher: any) => ({
        id: teacher._id.toString(),
        fullName: teacher.fullName,
        employeeId: teacher.employeeId,
        email: teacher.userId?.email || teacher.email,
        profilePicture: teacher.profilePicture,
        phone: teacher.phone,
        school: teacher.schoolId
          ? {
              id:
                teacher.schoolId._id?.toString() || teacher.schoolId.toString(),
              name: teacher.schoolId.schoolName || "Unknown",
            }
          : null,
      }));

      res.status(200).json({
        success: true,
        message: "Teachers retrieved successfully",
        data: {
          teachers: formattedTeachers,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get admin teachers for chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve teachers",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get filtered students for admin panel chat creation
   * @route GET /api/v1/admin/chats/students
   * @access Private (Admin)
   */
  async getAdminStudentsForChat(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { search, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      const query: any = {
        status: "active",
        isApproved: true,
      };

      // Search filter by name or email
      if (search && typeof search === "string") {
        const searchRegex = { $regex: search, $options: "i" };
        query.$or = [
          { fullName: searchRegex },
          { email: searchRegex },
          { studentId: searchRegex },
        ];
      }

      const [students, total] = await Promise.all([
        Student.find({ ...query })
          .populate("userId", "email")
          .populate("schoolId", "schoolName")
          .select(
            "fullName studentId profilePicture email phone academicLevel schoolId",
          )
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Student.countDocuments(query),
      ]);

      const formattedStudents = students.map((student: any) => ({
        id: student._id.toString(),
        fullName: student.fullName,
        studentId: student.studentId,
        email: student.userId?.email || student.email,
        profilePicture: student.profilePicture,
        phone: student.phone,
        academicLevel: student.academicLevel,
        school: student.schoolId
          ? {
              id:
                student.schoolId._id?.toString() || student.schoolId.toString(),
              name: student.schoolId.schoolName || "Unknown",
            }
          : null,
      }));

      res.status(200).json({
        success: true,
        message: "Students retrieved successfully",
        data: {
          students: formattedStudents,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get admin students for chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve students",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get filtered schools for admin panel chat creation
   * @route GET /api/v1/admin/chats/schools
   * @access Private (Admin)
   */
  async getAdminSchoolsForChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { search, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      );
      const skip = (pageNum - 1) * limitNum;

      const query: any = {
        status: "approved",
        isVerified: true,
      };

      // Search filter by name, code, or email
      if (search && typeof search === "string") {
        const searchRegex = { $regex: search, $options: "i" };
        query.$or = [
          { schoolName: searchRegex },
          { schoolCode: searchRegex },
          { email: searchRegex },
        ];
      }

      const [schools, total] = await Promise.all([
        School.find({ ...query })
          .populate("userId", "email")
          .select("schoolName schoolCode email phone address logo")
          .sort({ schoolName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        School.countDocuments(query),
      ]);

      const formattedSchools = schools.map((school: any) => ({
        id: school._id.toString(),
        schoolName: school.schoolName,
        schoolCode: school.schoolCode,
        email: school.userId?.email || school.email,
        phone: school.phone,
        logo: school.logo,
        address: school.address,
      }));

      res.status(200).json({
        success: true,
        message: "Schools retrieved successfully",
        data: {
          schools: formattedSchools,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get admin schools for chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve schools",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create chat from admin panel with teacher, student, or school
   * @route POST /api/v1/admin/chats
   * @access Private (Admin)
   */
  async createAdminChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { teacherId, studentId, schoolId, subject } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const providedIds = [teacherId, studentId, schoolId].filter(Boolean);
      if (providedIds.length !== 1) {
        res.status(400).json({
          success: false,
          message:
            "Exactly one of teacherId, studentId, or schoolId is required",
        } as APIResponse);
        return;
      }

      let chat: any;

      if (teacherId) {
        // Admin-Teacher chat
        const teacher = await Teacher.findById(teacherId);
        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher not found",
          } as APIResponse);
          return;
        }

        chat = await Chat.findOne({
          teacherId: teacher._id,
          type: CHAT_TYPE.ADMIN_TEACHER,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          chat = new Chat({
            schoolId: teacher.schoolId,
            teacherId: teacher._id,
            adminId: new mongoose.Types.ObjectId(userId),
            type: CHAT_TYPE.ADMIN_TEACHER,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || `Chat with ${teacher.fullName}`,
            participants: [new mongoose.Types.ObjectId(userId), teacher.userId],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }
      } else if (studentId) {
        // Admin-Student chat
        const student = await Student.findById(studentId);
        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student not found",
          } as APIResponse);
          return;
        }

        chat = await Chat.findOne({
          studentId: student._id,
          type: CHAT_TYPE.STUDENT_ADMIN,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          chat = new Chat({
            schoolId: student.schoolId,
            studentId: student._id,
            adminId: new mongoose.Types.ObjectId(userId),
            type: CHAT_TYPE.STUDENT_ADMIN,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || `Chat with ${student.fullName}`,
            participants: [new mongoose.Types.ObjectId(userId), student.userId],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }
      } else if (schoolId) {
        // Admin-School chat
        const school = await School.findById(schoolId);
        if (!school) {
          res.status(404).json({
            success: false,
            message: "School not found",
          } as APIResponse);
          return;
        }

        chat = await Chat.findOne({
          schoolId: school._id,
          type: CHAT_TYPE.SCHOOL_ADMIN,
          status: { $ne: CHAT_STATUS.CLOSED },
        });

        if (!chat) {
          const schoolUser = await mongoose
            .model("User")
            .findById(school.userId)
            .lean();

          if (!schoolUser) {
            res.status(404).json({
              success: false,
              message: "School user not found",
            } as APIResponse);
            return;
          }

          chat = new Chat({
            schoolId: school._id,
            adminId: new mongoose.Types.ObjectId(userId),
            type: CHAT_TYPE.SCHOOL_ADMIN,
            status: CHAT_STATUS.ACTIVE,
            subject: subject || `Chat with ${school.schoolName}`,
            participants: [
              new mongoose.Types.ObjectId(userId),
              (schoolUser as any)._id as mongoose.Types.ObjectId,
            ],
            unreadCount: { school: 0, student: 0, teacher: 0, admin: 0 },
          });
          await chat.save();
        }
      }

      const populatedChat = await Chat.findById(chat._id)
        .populate("student", "fullName studentId profilePicture")
        .populate("teacher", "fullName employeeId profilePicture")
        .populate("schoolId", "schoolName schoolCode logo")
        .lean();

      const chatDoc = populatedChat as any;
      const student = chatDoc?.student;
      const teacher = chatDoc?.teacher;
      const school = chatDoc?.schoolId;

      res.status(200).json({
        success: true,
        message: "Chat created successfully",
        data: {
          chat: {
            id: chat.id,
            type: chat.type,
            status: chat.status,
            subject: chat.subject,
            student: student
              ? {
                  id: student.id || student._id,
                  fullName: student.fullName,
                  studentId: student.studentId,
                  profilePicture: student.profilePicture,
                }
              : null,
            teacher: teacher
              ? {
                  id: teacher.id || teacher._id,
                  fullName: teacher.fullName,
                  employeeId: teacher.employeeId,
                  profilePicture: teacher.profilePicture,
                }
              : null,
            school: school
              ? {
                  id: school.id || school._id,
                  schoolName: school.schoolName,
                  schoolCode: school.schoolCode,
                  logo: school.logo,
                }
              : null,
            createdAt: chat.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create admin chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create chat",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  async markAsRead(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { chatId } = req.params;
      const userId = req.user?.userId;
      const role = req.user?.role as "school" | "student" | "teacher" | "admin";

      if (!userId || !role) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const chat = await Chat.findById(chatId);
      if (!chat) {
        res.status(404).json({ success: false, message: "Chat not found" });
        return;
      }

      if (!chat.participants.some(p => p.toString() === userId)) {
        res.status(403).json({ success: false, message: "Access denied" });
        return;
      }

      await chat.markAsRead(role);
      await redisService.resetUnreadCount(chatId, userId);

      res.status(200).json({
        success: true,
        message: "Conversation marked as read",
        data: { conversationId: chatId, readAt: new Date().toISOString() },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Mark chat read error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to mark conversation as read",
      } as APIResponse);
    }
  }

  async getClassGroupChat(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const classItem = await Class.findById(classId).populate(
        "teacherIds",
        "userId fullName",
      );
      if (!classItem) {
        res.status(404).json({ success: false, message: "Class not found" });
        return;
      }

      let chat = await Chat.findOne({
        classId: classItem._id,
        isGroup: true,
        type: CHAT_TYPE.GROUP,
      })
        .populate("participants", "email fullName profilePicture")
        .lean();

      if (!chat) {
        const participantUserIds = new Set<string>([userId]);
        for (const teacher of (classItem.teacherIds as unknown[]) ?? []) {
          const t = teacher as { userId?: mongoose.Types.ObjectId };
          if (t.userId) participantUserIds.add(t.userId.toString());
        }
        const students = await Student.find({
          _id: { $in: classItem.studentIds ?? [] },
        }).select("userId");
        for (const student of students) {
          participantUserIds.add(student.userId.toString());
        }

        const created = await Chat.create({
          classId: classItem._id,
          schoolId: classItem.schoolId,
          type: CHAT_TYPE.GROUP,
          status: CHAT_STATUS.ACTIVE,
          name: `${classItem.className} Group`,
          isGroup: true,
          createdBy: new mongoose.Types.ObjectId(userId),
          participants: [...participantUserIds].map(
            id => new mongoose.Types.ObjectId(id),
          ),
          admins: [new mongoose.Types.ObjectId(userId)],
        });
        chat = (await Chat.findById(created._id)
          .populate("participants", "email fullName profilePicture")
          .lean()) as typeof chat;
      }

      res.status(200).json({
        success: true,
        message: "Class group chat retrieved successfully",
        data: { conversation: chat },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get class group chat error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve class group chat",
      } as APIResponse);
    }
  }

  async addClassGroupMember(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      const { studentId } = req.body;

      if (!userId || !studentId) {
        res.status(400).json({
          success: false,
          message: "studentId is required",
        });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      const classItem = await Class.findOne({
        _id: classId,
        teacherIds: teacher?._id,
      });
      if (!classItem || !teacher) {
        res.status(403).json({ success: false, message: "Access denied" });
        return;
      }

      const student = await Student.findById(studentId);
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const chat = await Chat.findOne({
        classId: classItem._id,
        isGroup: true,
      });
      if (!chat) {
        res.status(404).json({
          success: false,
          message: "Group chat not found. Fetch group first.",
        });
        return;
      }

      await chat.addParticipant(student.userId);
      socketServer.emitToChat(chat.id, "member.added", {
        classId,
        studentId,
        userId: student.userId.toString(),
      });

      res.status(200).json({
        success: true,
        message: "Member added to group chat",
        data: { conversationId: chat.id, studentId },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Add class group member error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to add group member",
      } as APIResponse);
    }
  }

  async removeClassGroupMember(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, studentId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      const classItem = await Class.findOne({
        _id: classId,
        teacherIds: teacher?._id,
      });
      if (!classItem || !teacher) {
        res.status(403).json({ success: false, message: "Access denied" });
        return;
      }

      const student = await Student.findById(studentId);
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const chat = await Chat.findOne({
        classId: classItem._id,
        isGroup: true,
      });
      if (!chat) {
        res
          .status(404)
          .json({ success: false, message: "Group chat not found" });
        return;
      }

      await chat.removeParticipant(student.userId);
      socketServer.emitToChat(chat.id, "member.removed", {
        classId,
        studentId,
        userId: student.userId.toString(),
      });

      res.status(200).json({
        success: true,
        message: "Member removed from group chat",
        data: { conversationId: chat.id, studentId },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Remove class group member error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to remove group member",
      } as APIResponse);
    }
  }
}

export const chatController = new ChatController();
